import { motion } from "framer-motion";
import { CloudDrizzle, Filter, ScanLine, FlaskConical, Package } from "lucide-react";

const steps = [
  {
    icon: CloudDrizzle,
    label: "Sustainable Source",
    title: "The Sky",
    detail: "Over 12,900 cubic kilometres of moisture float in Earth's atmosphere at any moment. That is where our water begins.",
  },
  {
    icon: Filter,
    label: "Multi-Level Purification",
    title: "Layered Purity",
    detail: "Activated carbon filtration, sediment filtration, ozonation and UV disinfection — each layer removes what the next refines.",
  },
  {
    icon: ScanLine,
    label: "Pristine Filtration",
    title: "0.2 Microns",
    detail: "Ultra-fine filtration down to 0.2 microns — smaller than a strand of hair by hundreds of times.",
  },
  {
    icon: FlaskConical,
    label: "Mineralisation & Tailored pH",
    title: "Nature's Balance",
    detail: "A consistent pH above 7.5, enriched with essential minerals for taste and character.",
  },
  {
    icon: Package,
    label: "Elegant Bottling",
    title: "Ready for You",
    detail: "Eight elegant packaging options — glass and premium PET — every bottle sealed with the same standard of care.",
  },
];

export const PurityProcess = () => (
  <section data-testid="purity-process-section" className="py-24 md:py-32 lg:py-40 bg-[#F5F5F7]">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="text-center mb-16 md:mb-20 max-w-3xl mx-auto"
      >
        <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">
          Purity Assurance
        </p>
        <h2
          data-testid="purity-process-heading"
          className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]"
        >
          Precision, efficiency, and an unwavering dedication to excellence.
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
          Five stages between the sky and your glass — each one measured, monitored, and never rushed.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
        {steps.map((s, i) => (
          <motion.div
            key={s.label}
            data-testid={`purity-step-${i}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            className="relative bg-white rounded-3xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            <span className="font-display text-4xl font-bold text-[#2C3E35]/15">{`0${i + 1}`}</span>
            <div className="w-11 h-11 rounded-2xl bg-[#2C3E35]/10 flex items-center justify-center mt-3 mb-5">
              <s.icon className="w-5 h-5 text-[#2C3E35]" strokeWidth={1.6} />
            </div>
            <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-[#86868B] mb-1.5">{s.label}</p>
            <h3 className="font-display text-lg md:text-xl tracking-tight font-medium text-[#1D1D1F] mb-3">{s.title}</h3>
            <p className="text-sm leading-relaxed text-[#86868B]">{s.detail}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
